import * as React from 'react';
import { useSelector } from 'react-redux';

import { getPaginationUrl, preparePagination } from '../../components/Pagination/utils';
import { IPaginationItem } from '../../types/pagination';
import { selectQueryString, selectTotal } from '../../selectors/offersData';
import { NEWBUILDINGS_GK_SUGGEST_COUNT } from '../../constants/config';
import { getCurrentPage, selectGeoPath } from '../../selectors/location';
import { selectSeoQuery } from '../../selectors/seo';

interface IPagination {
  pages: IPaginationItem[];
  nextPage?: IPaginationItem;
  prevPage?: IPaginationItem;
  totalPages: number;
}

export function usePagination(): IPagination {
  const total = useSelector(selectTotal) || 0;
  const page = useSelector(getCurrentPage) || 1;
  const geoPath = useSelector(selectGeoPath);
  const seoQuery = useSelector(selectSeoQuery);
  const queryString = useSelector(selectQueryString);
  const finalQueryString = seoQuery || queryString;

  const totalPages = React.useMemo(() => Math.ceil(total / NEWBUILDINGS_GK_SUGGEST_COUNT), [total]);

  const pages = React.useMemo(
    () => (totalPages <= 1 ? [] : preparePagination({ currentPage: page, totalPages, finalQueryString, geoPath })),
    [page, finalQueryString, geoPath, totalPages],
  );

  const prevPage = React.useMemo<IPaginationItem>(
    () => ({
      disabled: page === 1,
      page: page - 1,
      url: getPaginationUrl({ page: page - 1, geoPath, finalQueryString }),
      title: 'Назад',
    }),
    [page, geoPath, finalQueryString],
  );

  const nextPage = React.useMemo<IPaginationItem>(
    () => ({
      disabled: page === totalPages,
      page: page + 1,
      url: getPaginationUrl({ page: page + 1, geoPath, finalQueryString }),
      title: 'Дальше',
    }),
    [finalQueryString, geoPath, page, totalPages],
  );

  return React.useMemo(
    () => ({
      totalPages,
      nextPage,
      prevPage,
      pages,
    }),
    [nextPage, pages, prevPage, totalPages],
  );
}
